import React, { Component } from 'react';
import { connect } from 'react-redux';

import classes from './Player.module.css';
import * as actions from '../../store/actions/index';
import Playbutton from '../../UI/Buttons/Playbutton';
import Pausebutton from '../../UI/Buttons/Pausebutton';

// Inline SVGs — guaranteed to render on all browsers including Safari
const IconHigh = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
    </svg>
);
const IconLow = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
    </svg>
);
const IconMute = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
    </svg>
);

class Player extends Component {

    constructor(props) {
        super(props);
        this.audioRef = React.createRef();
        this.lastSeekUpdate = 0;
        this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        this.state = {
            currentTime: 0,
            sliderMax: 0,
            volume: 1,
            muted: false,
        };
    }

    componentDidMount() {
        const audio = this.audioRef.current;
        if (audio) {
            audio.load();
            audio.addEventListener('timeupdate', this.handleTimeUpdate);
        }
    }

    componentWillUnmount() {
        const audio = this.audioRef.current;
        if (audio) {
            audio.removeEventListener('timeupdate', this.handleTimeUpdate);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.streamUrl !== this.props.streamUrl) {
            const audio = this.audioRef.current;
            if (!audio) return;
            audio.load();
            if (this.props.play) {
                audio.play().catch(err => console.error('Playback failed:', err));
            }
        }
    }

    handleTimeUpdate = () => {
        const audio = this.audioRef.current;
        if (!audio) return;
        const now = Date.now();
        if (now - this.lastSeekUpdate < 1000) return;
        this.lastSeekUpdate = now;
        const seekable = audio.seekable;
        if (seekable.length > 0) {
            const end = seekable.end(0);
            const liveEdge = isFinite(end) ? end : audio.currentTime;
            this.setState(prev => ({
                currentTime: audio.currentTime,
                sliderMax: Math.max(prev.sliderMax, liveEdge),
            }));
        }
    }

    handleSeek = (e) => {
        const audio = this.audioRef.current;
        if (audio) {
            audio.currentTime = parseFloat(e.target.value);
            this.lastSeekUpdate = 0;
        }
    }

    handleGoLive = () => {
        const audio = this.audioRef.current;
        if (!audio) return;
        if (audio.seekable.length > 0) {
            const end = audio.seekable.end(0);
            audio.currentTime = isFinite(end) ? end : this.state.sliderMax;
            this.lastSeekUpdate = 0;
        }
    }

    handleVolumeChange = (e) => {
        const vol = parseFloat(e.target.value);
        this.setState({ volume: vol, muted: vol === 0 });
        const audio = this.audioRef.current;
        if (audio) {
            audio.volume = vol;
            audio.muted = false;
        }
    }

    handleMuteToggle = () => {
        const audio = this.audioRef.current;
        if (!audio) return;
        const newMuted = !this.state.muted;
        this.setState({ muted: newMuted });
        audio.muted = newMuted;
    }

    togglePlay = () => {
        const audio = this.audioRef.current;
        const newPlay = !this.props.play;
        this.props.onChange(newPlay);
        if (audio) {
            if (newPlay) {
                audio.play().catch(err => console.error('Playback failed:', err));
            } else {
                audio.pause();
            }
        }
    }

    render() {
        const { play, currentProgram, streamUrl } = this.props;
        const { currentTime, sliderMax, volume, muted } = this.state;
        const src = streamUrl;

        const hasSeekRange = sliderMax > 0;
        const behindLive = sliderMax - currentTime;
        const isAtLive = !hasSeekRange || behindLive < 5;

        const seekFill = hasSeekRange
            ? `${Math.min(100, (currentTime / sliderMax) * 100).toFixed(1)}%`
            : '100%';
        const volFill = `${((muted ? 0 : volume) * 100).toFixed(1)}%`;

        const formatOffset = (secs) => {
            if (!isFinite(secs) || isNaN(secs) || secs < 1) return '';
            const s = Math.floor(secs);
            const m = Math.floor(s / 60);
            const ss = s % 60;
            return `-${m}:${ss.toString().padStart(2, '0')}`;
        };

        const VolumeIcon = (muted || volume === 0) ? IconMute : volume < 0.5 ? IconLow : IconHigh;

        return (
            <div className={classes.background}>
                <div className={classes.Player}>
                    <div className={`${classes.Play} ${play ? classes.spinning : ''}`}>
                        {play
                            ? <Pausebutton click={this.togglePlay}/>
                            : <Playbutton click={this.togglePlay}/>
                        }
                    </div>
                    <audio ref={this.audioRef} src={src} />
                    <div className={classes.infobox}>
                        <div className={classes.program}>
                            <h2>Studiossa:</h2>
                            <div className={classes.text}>
                                <span className={classes.scrollingText}>
                                    {currentProgram.programName}{currentProgram.groupName ? ` - ${currentProgram.groupName}` : ''}
                                </span>
                            </div>
                        </div>
                        <div className={classes.controls}>
                            <span className={classes.barLabel}>
                                {hasSeekRange && !isAtLive ? formatOffset(behindLive) : ''}
                            </span>
                            <div className={classes.sliderWrap}>
                                <input
                                    type="range"
                                    className={classes.slider}
                                    style={{ '--fill': seekFill, opacity: hasSeekRange ? 1 : 0.3 }}
                                    min={0}
                                    max={sliderMax || 1}
                                    step={1}
                                    value={currentTime}
                                    onChange={this.handleSeek}
                                    disabled={!hasSeekRange}
                                />
                            </div>
                            <button
                                className={`${classes.liveBtn} ${!isAtLive ? classes.liveBtnActive : ''}`}
                                onClick={this.handleGoLive}
                                disabled={isAtLive}
                            >&#9679;&nbsp;LIVE</button>
                            <button className={classes.muteBtn} onClick={this.handleMuteToggle} aria-label="Mute">
                                <VolumeIcon />
                            </button>
                            {this.isIOS ? (
                                <div className={classes.sliderWrap} />
                            ) : (
                                <div className={classes.sliderWrap}>
                                    <input
                                        type="range"
                                        className={classes.slider}
                                        style={{ '--fill': volFill }}
                                        min={0}
                                        max={1}
                                        step={0.02}
                                        value={muted ? 0 : volume}
                                        onChange={this.handleVolumeChange}
                                    />
                                </div>
                            )}
                            <span className={classes.barSpacer} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    play: state.play.play,
    currentProgram: state.play.program,
    streamUrl: state.information.data && state.information.data[0]
        ? state.information.data[0].streamUrl
        : null
});

const mapDispatchToProps = dispatch => ({
    onChange: (play) => dispatch(actions.changeStatePlay(play))
});

export default connect(mapStateToProps, mapDispatchToProps)(Player);
